import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { printRoundKOT } from "../lib/kot";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Volume2, VolumeX, X, Printer } from "lucide-react";

// Kitchen Board — a wall screen read at 3 metres by a chef with busy hands. Full-bleed, dark, no
// AppLayout chrome. Live over the orders WebSocket (check.* + order.*), with reconnect-on-backoff
// and a 30s poll of /kitchen/board as belt-and-braces. Every figure/ticket comes from the server;
// the only per-device preference is the sound mute (localStorage). No prices anywhere.

const POLL_MS = 30000;
const WS_MAX_BACKOFF = 15000;
const READY_CLEAR_MS = 10 * 60 * 1000;   // a ready ticket lingers 10 min then clears
const AMBER_MIN = 10, RED_MIN = 20;      // hardcoded, no venue setting

function wsUrl() {
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return base.replace(/^http/, "ws") + "/api/ws/orders";
}
const ORDER_TO_KITCHEN = { pending: "new", preparing: "preparing", ready: "ready" };

// Colour + label per order type for the ticket badge (matches the Orders board palette). There is
// no "online" type any more — every order is dine-in, takeaway or room (G-6).
const TYPE_META = {
  dine_in:  { label: "DINE-IN",  cls: "bg-[#9D00FF]/20 text-[#D78CFF] border-[#9D00FF]/50" },
  takeaway: { label: "TAKEAWAY", cls: "bg-white/10 text-[#D0D0D0] border-white/25" },
  room:     { label: "ROOM",     cls: "bg-sky-500/20 text-sky-300 border-sky-500/50" },
};
// Badge text: "DINE-IN · <table>", "ROOM 301", or "TAKEAWAY". location already carries the
// table/room label; a named channel, when present, rides along in that location string.
function ticketBadge(t) {
  const meta = TYPE_META[t.order_type] || TYPE_META.takeaway;
  if (t.order_type === "dine_in") return { ...meta, text: t.location ? `${meta.label} · ${t.location}` : meta.label };
  if (t.order_type === "room")    return { ...meta, text: (t.location || meta.label).toUpperCase() };
  return { ...meta, text: meta.label };
}

// ---- map a raw WS order doc to the board's ticket shape (mirrors GET /kitchen/board) ----
function orderToTicket(o) {
  return {
    source: "order", ticket_id: o.id, order_id: o.id, kot_number: null,
    display_number: `#${o.order_number}`,
    location: o.order_type === "room" ? `Room ${o.room_number || ""}`.trim() : "Takeaway",
    order_type: o.order_type, covers: null, steward_name: null,
    // Full ticket context (G-6), mirroring GET /kitchen/board: order note, customer name
    // (first name shown), vehicle for takeaway. No prices.
    note: o.special_note || null,
    customer_name: o.customer_name || null,
    vehicle_number: o.order_type === "takeaway" ? (o.vehicle_number || null) : null,
    placed_at: o.created_at, status: ORDER_TO_KITCHEN[o.status] || "new",
    items: (o.items || []).map((it, i) => ({
      line_id: it.line_id || `${o.id}:${i}`, name: it.name, qty: it.qty,
      note: it.note || null, addons: it.addons || [], variant_label: it.variant_label,
      cancelled: false, cancelled_reason: null,
    })),
  };
}
// ---- a check WS payload + its round → ticket ----
function checkRoundToTicket(cp, rnd) {
  return {
    source: "check", ticket_id: `${cp.check_id}:${rnd.round_no}`,
    check_id: cp.check_id, round_no: rnd.round_no, kot_number: rnd.kot_number,
    display_number: `KOT ${rnd.kot_number}`,
    location: (cp.service_point_labels || []).join(" + ") || "Table",
    order_type: "dine_in", covers: cp.covers, steward_name: cp.steward_name,
    placed_at: rnd.placed_at, status: rnd.kitchen_status || "new", items: rnd.items || [],
  };
}

function minutesSince(iso) {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
}

// A short chime via WebAudio (no bundled asset). Unlocked on first tap; never throws. `mutedRef`
// is read live so `play` stays referentially stable — it must not re-create the WS effect.
function useChime(mutedRef) {
  const ctxRef = useRef(null);
  const unlock = useCallback(() => {
    try {
      if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    } catch (_) { /* audio unavailable — ignore */ }
  }, []);
  const play = useCallback(() => {
    if (mutedRef.current) return;
    try {
      const ctx = ctxRef.current;
      if (!ctx) return;
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + 0.36);
    } catch (_) { /* ignore */ }
  }, [mutedRef]);
  return { unlock, play };
}

export default function Kitchen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Kitchen board is optional per venue (G-6). `showStart` adds the START step; default off → one
  // READY action. When the board is disabled there is nothing to show here.
  const boardEnabled = user?.venue?.kitchen_board_enabled;
  const showStart = !!user?.venue?.kitchen_show_start;
  const [tickets, setTickets] = useState([]);          // array of ticket objects (+ client-only fields)
  const [connected, setConnected] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [downSince, setDownSince] = useState(null);
  const [muted, setMuted] = useState(() => { try { return localStorage.getItem("kds_muted") === "1"; } catch { return false; } });
  const [nowTick, setNowTick] = useState(Date.now());   // re-render every 10s so ages/timers advance

  const ackedRef = useRef(new Set());                   // line_ids whose cancellation has been acknowledged
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const { unlock, play } = useChime(mutedRef);

  // ---- ticket store helpers (operate on the ref so WS handlers are stable) ----
  const setStore = useCallback((updater) => {
    setTickets((prev) => updater(prev));
  }, []);
  const upsert = useCallback((t, { chime = false } = {}) => {
    setStore((prev) => {
      const i = prev.findIndex((x) => x.ticket_id === t.ticket_id);
      const existing = i >= 0 ? prev[i] : null;
      // preserve client-only fields across replacement
      const merged = { ...t, _flash: existing?._flash, _cancelledExtra: existing?._cancelledExtra || [], _readyAt: existing?._readyAt };
      if (t.status === "ready" && !merged._readyAt) merged._readyAt = Date.now();
      if (t.status !== "ready") merged._readyAt = undefined;
      const next = [...prev];
      if (i >= 0) next[i] = merged; else next.push(merged);
      return next;
    });
    if (chime) play();
  }, [setStore, play]);
  const removeByCheck = useCallback((checkId) => {
    setStore((prev) => prev.filter((t) => t.check_id !== checkId));
  }, [setStore]);
  const removeTicket = useCallback((ticketId) => {
    setStore((prev) => prev.filter((t) => t.ticket_id !== ticketId));
  }, [setStore]);

  // ---- full reconcile from the API (mount, 30s poll, reconnect) ----
  const reconcile = useCallback(async () => {
    try {
      const { data } = await api.get("/kitchen/board");
      const server = data.tickets || [];
      setStore((prev) => {
        const prevById = Object.fromEntries(prev.map((t) => [t.ticket_id, t]));
        return server.map((t) => {
          const old = prevById[t.ticket_id];
          const merged = { ...t, _flash: old?._flash, _cancelledExtra: old?._cancelledExtra || [], _readyAt: old?._readyAt };
          if (t.status === "ready" && !merged._readyAt) merged._readyAt = Date.now();
          if (t.status !== "ready") merged._readyAt = undefined;
          return merged;
        });
      });
      setLastUpdated(Date.now());
    } catch (_) { /* leave the last good board up; the bar shows staleness */ }
  }, [setStore]);

  // ---- WebSocket with reconnect backoff + ping/pong staleness ----
  const wsRef = useRef(null);
  const backoffRef = useRef(1000);
  const wantRef = useRef(true);
  const lastMsgRef = useRef(Date.now());

  const handleMessage = useCallback((msg) => {
    lastMsgRef.current = Date.now();
    setLastUpdated(Date.now());
    const t = msg.type;
    if (t === "pong") return;
    if (t === "order.created" || t === "order.updated") {
      const o = msg.order || {};
      if (!["takeaway", "room"].includes(o.order_type)) return;   // dine-in flows through checks
      // Same gate as the Orders board (H-3): an order awaiting an online payment never reaches the
      // kitchen. It appears here only once paid (an order.updated flips payment_status off "pending").
      const showable = ["pending", "preparing"].includes(o.status) && o.payment_status !== "pending";
      if (showable) upsert(orderToTicket(o), { chime: t === "order.created" });
      else removeTicket(o.id);   // awaiting / ready / collected / cancelled / delivered leave the board
      return;
    }
    if (t === "check.round_added") {
      const cp = msg.check || {};
      if (cp.round) upsert(checkRoundToTicket(cp, cp.round), { chime: true });
      return;
    }
    if (t === "check.round_status") {
      const cp = msg.check || {};
      if (cp.round) upsert(checkRoundToTicket(cp, cp.round));
      return;
    }
    if (t === "check.round_updated") {
      const cp = msg.check || {};
      if (!cp.round) return;
      const ticket = checkRoundToTicket(cp, cp.round);
      const ch = msg.change || {};
      // A removed (cancelled) line is gone from round.items — inject a struck placeholder so the
      // chef is told to stop, and flash the card. Comped lines are already flagged in items.
      setStore((prev) => {
        const i = prev.findIndex((x) => x.ticket_id === ticket.ticket_id);
        const old = i >= 0 ? prev[i] : null;
        const extra = [...(old?._cancelledExtra || [])];
        if (ch.kind === "cancelled" && ch.line_id && !ackedRef.current.has(ch.line_id)) {
          if (!extra.some((e) => e.line_id === ch.line_id)) {
            extra.push({ line_id: ch.line_id, name: ch.name, qty: ch.qty, cancelled: true, cancelled_reason: ch.reason, _removed: true });
          }
        }
        if (ch.kind === "qty") {
          ticket.items = ticket.items.map((it) => it.line_id === ch.line_id ? { ...it, _qtyFrom: ch.qty_before, _qtyTo: ch.qty_after } : it);
        }
        const merged = { ...ticket, _cancelledExtra: extra, _flash: Date.now(), _readyAt: old?._readyAt };
        const next = [...prev];
        if (i >= 0) next[i] = merged; else next.push(merged);
        return next;
      });
      return;
    }
    if (t === "check.closed") {
      const cp = msg.check || {};
      if (cp.check_id) removeByCheck(cp.check_id);
      return;
    }
  }, [upsert, removeTicket, removeByCheck, setStore]);

  useEffect(() => {
    wantRef.current = true;
    let pingTimer = null, staleTimer = null;
    const connect = () => {
      if (!wantRef.current) return;
      let ws;
      try { ws = new WebSocket(wsUrl()); } catch (_) { scheduleReconnect(); return; }
      wsRef.current = ws;
      ws.onopen = () => {
        backoffRef.current = 1000;
        setConnected(true); setDownSince(null);
        lastMsgRef.current = Date.now();
        reconcile();   // resync on (re)connect
      };
      ws.onmessage = (ev) => { try { handleMessage(JSON.parse(ev.data)); } catch (_) {} };
      ws.onerror = () => { try { ws.close(); } catch (_) {} };
      ws.onclose = () => {
        setConnected(false);
        setDownSince((d) => d || Date.now());
        scheduleReconnect();
      };
    };
    const scheduleReconnect = () => {
      if (!wantRef.current) return;
      const delay = backoffRef.current;
      backoffRef.current = Math.min(backoffRef.current * 2, WS_MAX_BACKOFF);
      setTimeout(connect, delay);
    };
    connect();
    // application-level ping so a silently-dead socket is detected
    pingTimer = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === 1) { try { ws.send(JSON.stringify({ type: "ping" })); } catch (_) {} }
    }, 20000);
    staleTimer = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === 1 && Date.now() - lastMsgRef.current > 45000) {
        try { wsRef.current.close(); } catch (_) {}   // no traffic → force reconnect
      }
    }, 10000);
    const pollTimer = setInterval(reconcile, POLL_MS);
    return () => {
      wantRef.current = false;
      clearInterval(pingTimer); clearInterval(staleTimer); clearInterval(pollTimer);
      if (wsRef.current) { try { wsRef.current.onclose = null; wsRef.current.close(); } catch (_) {} }
    };
  }, [handleMessage, reconcile]);

  // ---- tick every 10s for ages/timers; clear ready tickets after 10 min ----
  useEffect(() => {
    const t = setInterval(() => {
      setNowTick(Date.now());
      setStore((prev) => prev.filter((tk) => !(tk._readyAt && Date.now() - tk._readyAt > READY_CLEAR_MS)));
    }, 10000);
    return () => clearInterval(t);
  }, [setStore]);

  // ---- board optional (G-6): a venue with it off has no board — send staff to Orders ----
  useEffect(() => {
    if (user?.venue && !boardEnabled) navigate("/orders", { replace: true });
  }, [user, boardEnabled, navigate]);

  // ---- screen wake lock (best-effort) ----
  useEffect(() => {
    let lock = null;
    const acquire = async () => { try { lock = await navigator.wakeLock.request("screen"); } catch (_) {} };
    if ("wakeLock" in navigator) {
      acquire();
      const onVis = () => { if (document.visibilityState === "visible") acquire(); };
      document.addEventListener("visibilitychange", onVis);
      return () => { document.removeEventListener("visibilitychange", onVis); try { lock && lock.release(); } catch (_) {} };
    }
  }, []);

  const toggleMute = () => setMuted((m) => { const n = !m; try { localStorage.setItem("kds_muted", n ? "1" : "0"); } catch {} return n; });

  // ---- actions ----
  const setCheckStatus = async (t, status, undo = false) => {
    try { await api.post(`/checks/${t.check_id}/rounds/${t.round_no}/kitchen-status`, { status, undo }); }
    catch (_) {}
  };
  const setOrderStatus = async (t, status, undo = false) => {
    // undo:true lets the order endpoint allow the one backward move (ready → preparing); a plain
    // backward move is 409 now that orders mirror the checks state machine (G-4).
    try { await api.patch(`/orders/${t.order_id}/status`, { status, undo }); }
    catch (_) {}
  };
  const onStart = (t) => t.source === "check" ? setCheckStatus(t, "preparing") : setOrderStatus(t, "preparing");
  const onReady = (t) => t.source === "check" ? setCheckStatus(t, "ready") : setOrderStatus(t, "ready");
  const onUndo = (t) => t.source === "check" ? setCheckStatus(t, "preparing", true) : setOrderStatus(t, "preparing", true);
  const onClearReady = (t) => removeTicket(t.ticket_id);
  // Print the kitchen docket for a dine-in round (G-8). Server stamps printed_at + says if it's a
  // reprint; the KOT (no prices) renders from the ticket we already hold. Dine-in rounds only.
  const printKot = async (t) => {
    if (t.source !== "check") return;
    try {
      const { data } = await api.post(`/checks/${t.check_id}/rounds/${t.round_no}/print-kot`);
      await printRoundKOT({
        kotNumber: t.kot_number, placedAt: t.placed_at, items: t.items || [],
        venueName: user?.venue?.name || "", location: t.location, reprint: !!data.reprint,
        widthMm: user?.venue?.receipt_width_mm || 80,
        refType: "check", refId: `${t.check_id}:${t.round_no}`,
      });
    } catch (_) {
      // The print helper surfaces a print failure itself; this catches the print-kot request
      // failing (so no docket was stamped). Either way the chef must know — never swallow it.
      toast.error("Couldn't print the kitchen docket. Please try again.");
    }
  };
  const ackLine = (t, lineId) => {
    ackedRef.current.add(lineId);
    setStore((prev) => prev.map((x) => x.ticket_id === t.ticket_id
      ? { ...x, _cancelledExtra: (x._cancelledExtra || []).filter((e) => e.line_id !== lineId),
          items: x.items.map((it) => it.line_id === lineId ? { ...it, _acked: true } : it) }
      : x));
  };

  // ---- sort: flashing cancellations first, then oldest first ----
  const visible = [...tickets].sort((a, b) => {
    const af = a._flash ? 0 : 1, bf = b._flash ? 0 : 1;
    if (af !== bf) return af - bf;
    return String(a.placed_at || "").localeCompare(String(b.placed_at || ""));
  });

  const downSecs = downSince ? Math.floor((nowTick - downSince) / 1000) : 0;

  // Board disabled for this venue → render nothing while the redirect effect sends staff to Orders.
  if (user?.venue && !boardEnabled) return null;

  return (
    <div onClick={unlock} className="min-h-screen bg-[#0A0A0A] text-white select-none" style={{ padding: "12px 16px" }}>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/orders")} className="w-11 h-11 flex items-center justify-center text-[#777] hover:text-white" aria-label="Exit board"><X size={22} /></button>
          <span className="text-xl font-black tracking-tight">Kitchen · {user?.venue?.name || ""}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-semibold ${connected ? "text-emerald-400" : "text-amber-400"}`}>
            {connected ? "● Live" : "○ Offline"}
          </span>
          <button onClick={toggleMute} className="w-12 h-12 flex items-center justify-center rounded-lg bg-white/5 border border-white/10" aria-label={muted ? "Unmute" : "Mute"}>
            {muted ? <VolumeX size={22} className="text-[#999]" /> : <Volume2 size={22} className="text-emerald-400" />}
          </button>
        </div>
      </div>

      {/* Connection bar — never silently stale */}
      {!connected && (
        <div className={`rounded-lg px-4 py-2 mb-3 text-base font-bold ${downSecs > 60 ? "bg-red-600/90" : "bg-amber-500/90 text-black"}`}>
          Reconnecting — last updated {new Date(lastUpdated).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          {downSecs > 60 ? " · connection lost over a minute ago" : ""}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="flex items-center justify-center h-[70vh] text-2xl text-[#555]">No tickets. Kitchen is clear.</div>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
          {visible.map((t) => (
            <TicketCard key={t.ticket_id} t={t} nowTick={nowTick} showStart={showStart}
              onStart={() => onStart(t)} onReady={() => onReady(t)} onUndo={() => onUndo(t)}
              onClearReady={() => onClearReady(t)} ackLine={(lid) => ackLine(t, lid)}
              onPrintKot={() => printKot(t)} />
          ))}
        </div>
      )}
    </div>
  );
}

function TicketCard({ t, nowTick, showStart, onStart, onReady, onUndo, onClearReady, ackLine, onPrintKot }) {
  const age = minutesSince(t.placed_at);
  const urgent = age >= RED_MIN, warn = age >= AMBER_MIN && age < RED_MIN;
  const border = urgent ? "border-red-500" : warn ? "border-amber-400" : "border-white/10";
  const timer = urgent ? "text-red-400" : warn ? "text-amber-300" : "text-[#A0A0A0]";
  const placedTime = t.placed_at ? new Date(t.placed_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";
  const badge = ticketBadge(t);
  // Customer first name only (takeaway/room tickets carry customer_name; checks don't).
  const custFirst = (t.customer_name || "").trim().split(/\s+/)[0] || "";

  // flash for ~1.2s after a change
  const flashing = t._flash && nowTick - t._flash < 1200;
  const liveItems = (t.items || []).filter((it) => !(it.cancelled && it._acked));
  // A cancelled line now arrives tombstoned IN round.items (G-2); the live `change` hint also injects
  // a struck placeholder. Drop any placeholder whose line is already present so it renders once.
  const liveIds = new Set(liveItems.map((it) => it.line_id));
  const extra = (t._cancelledExtra || []).filter((e) => !liveIds.has(e.line_id));
  const activeCount = liveItems.filter((it) => !it.cancelled).length;
  const everythingCancelled = activeCount === 0 && (liveItems.some((it) => it.cancelled) || extra.length > 0);

  if (t.status === "ready") {
    return (
      <button onClick={onClearReady}
        className="text-left rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 flex items-center justify-between">
        <span className="text-lg font-black">{t.location} · {t.display_number}</span>
        <span className="flex items-center gap-2">
          <span className="text-sm font-bold text-emerald-300">READY</span>
          <span onClick={(e) => { e.stopPropagation(); onUndo(); }} className="text-xs text-[#9D00FF] px-3 py-2 min-h-[44px] flex items-center">Undo</span>
        </span>
      </button>
    );
  }

  return (
    <div className={`rounded-xl border-2 ${everythingCancelled ? "border-red-500" : border} bg-[#141414] overflow-hidden ${flashing ? "ring-4 ring-red-500" : ""}`}>
      {/* Header */}
      <div className={`px-3 py-2 ${t.status === "preparing" ? "opacity-70" : ""}`} style={{ background: "rgba(255,255,255,0.03)" }}>
        <div className="flex items-baseline justify-between gap-2">
          <span className={`text-xl font-black uppercase tracking-wide leading-none px-2 py-1 rounded border ${badge.cls}`}>{badge.text}</span>
          <span className="flex items-center gap-2 shrink-0">
            {/* Print KOT — dine-in rounds only; a reprint is marked ** REPRINT ** by the server (G-8). */}
            {t.source === "check" && (
              <button onClick={(e) => { e.stopPropagation(); onPrintKot(); }}
                className="flex items-center gap-1 text-[11px] font-bold text-[#888] hover:text-white border border-white/10 hover:border-white/30 rounded px-2 py-1 min-h-[32px]"
                aria-label="Print KOT"><Printer size={12} /> KOT</button>
            )}
            <span className="text-lg font-bold text-[#B0B0B0]">{t.display_number}</span>
          </span>
        </div>
        <div className="flex items-center justify-between mt-1 gap-2">
          <span className="text-xs text-[#888] uppercase tracking-wide truncate">
            {custFirst ? `${custFirst} · ` : ""}{t.vehicle_number ? `${t.vehicle_number} · ` : ""}{t.covers ? `${t.covers} covers · ` : ""}{t.steward_name || ""}{t.steward_name ? " · " : ""}{placedTime}
          </span>
          <span className={`text-xl font-black ${timer} shrink-0`}>{age}m</span>
        </div>
        {/* Order-level note — amber, distinct from the grey per-item notes below. */}
        {t.note && (
          <div className="mt-1.5 flex items-start gap-1.5">
            <span className="shrink-0 text-[10px] font-black uppercase bg-amber-400 text-black px-1.5 py-0.5 rounded">Note</span>
            <span className="text-base font-bold text-amber-300 leading-snug">{t.note}</span>
          </div>
        )}
        {t.status === "preparing" && <span className="inline-block mt-1 text-[11px] font-bold uppercase tracking-wide bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded">Preparing</span>}
      </div>

      {everythingCancelled && (
        <div className="bg-red-600 text-white text-center font-black py-2 text-lg">CANCELLED — DO NOT PREPARE</div>
      )}

      {/* Items */}
      <div className="px-3 py-2 flex flex-col gap-1.5">
        {liveItems.map((it) => (
          <ItemRow key={it.line_id} it={it} onAck={() => ackLine(it.line_id)} />
        ))}
        {extra.map((it) => (
          <ItemRow key={it.line_id} it={it} onAck={() => ackLine(it.line_id)} />
        ))}
      </div>

      {/* Buttons — READY is the one action. START is shown only when the venue opts in via
          kitchen_show_start (default off); without it a ticket goes straight new → ready (G-6). */}
      {!everythingCancelled && (
        showStart ? (
          <div className="grid grid-cols-2 gap-2 p-2 border-t border-white/5">
            {t.status === "new"
              ? <button onClick={onStart} className="bg-white/10 hover:bg-white/15 text-white font-black rounded-lg py-4 text-lg" style={{ minHeight: 64 }}>START</button>
              : <div className="rounded-lg py-4 text-center text-[#666] text-lg" style={{ minHeight: 64 }}>Started</div>}
            <button onClick={onReady} className="bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-lg py-4 text-lg" style={{ minHeight: 64 }}>READY</button>
          </div>
        ) : (
          <div className="p-2 border-t border-white/5">
            <button onClick={onReady} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-lg py-4 text-lg" style={{ minHeight: 64 }}>READY</button>
          </div>
        )
      )}
      {everythingCancelled && (
        <div className="p-2 border-t border-white/5">
          <button onClick={() => (extra.concat(liveItems.filter((i) => i.cancelled))).forEach((i) => ackLine(i.line_id))}
            className="w-full bg-red-600/80 hover:bg-red-600 text-white font-black rounded-lg py-4 text-lg" style={{ minHeight: 64 }}>Got it — clear</button>
        </div>
      )}
    </div>
  );
}

function ItemRow({ it, onAck }) {
  const struck = it.cancelled;
  const addons = (it.addons || []).map((a) => a.name).filter(Boolean);
  const mods = [it.variant_label, ...addons, it.note].filter(Boolean).join(", ");
  return (
    <div className={`flex items-start gap-2 ${struck ? "text-red-400" : ""}`}>
      <span className="text-2xl font-black w-8 text-right shrink-0">{it.qty}</span>
      <div className="min-w-0 flex-1">
        <div className={`text-2xl font-bold leading-tight ${struck ? "line-through" : ""}`}>
          {it.name}
          {it._qtyTo !== undefined && <span className="text-base font-semibold text-amber-300"> was {it._qtyFrom} → now {it._qtyTo}</span>}
        </div>
        {mods && <div className="text-base text-[#9a9a9a] indent-2">{mods}</div>}
        {struck && <div className="text-sm font-bold text-red-400 uppercase">Cancelled{it.cancelled_reason ? ` — ${it.cancelled_reason}` : ""}</div>}
      </div>
      {struck && (
        <button onClick={onAck} className="text-xs font-bold text-white bg-red-600/60 rounded px-3 min-h-[44px]">Got it</button>
      )}
    </div>
  );
}
